import { isEmpty } from "lodash";
import moment from "moment";
import { getAPIFilters, getValueFilters } from "PFApp/use_filtered_collection/format_filters";
import { UseFiltersReturn } from "PFApp/use_filtered_collection/use_filters";
import useStorageFilters from "PFApp/use_filtered_collection/use_storage_filters";
import useStorageOrder, { OrderStorage } from "PFApp/use_filtered_collection/use_storage_order";
import { usePage } from "PFApp/use_page";
import { useProfiles } from "PFCore/hooks/queries/profiles/use_profiles";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { FetchProfilesResponse } from "PFCore/services/profile";
import { OrderOption } from "PFTypes";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";

export type PlannerPageContext = UseFiltersReturn &
  OrderStorage & { params: Params; setParams: Dispatch<SetStateAction<Params>> } & {
    loading: boolean;
    loaded: boolean;
    data: FetchProfilesResponse;
  };
type Params = { full: boolean; page: number; perPage: number; order_target: "capacity_index" };

const initialData = {
  entries: [],
  meta: { page: 0, perPage: 0, totalPages: 1, total: 0 }
};
const initParams = { perPage: 0, page: 1 };
const defaultParams: Params = {
  full: true,
  perPage: 25,
  page: 1,
  order_target: "capacity_index"
};

export const usePlannerTab = () => {
  const storagePrefix = "workflow_planner_v3";
  const { formatISODate } = useDateFormatter();
  const { page, mergePage } = usePage<{
    planner: PlannerPageContext;
  }>(`workflowPage`);

  const { current: defaultOrder } = useRef({
    direction: "desc",
    end_date: formatISODate(moment().add(1, "months")),
    name: ["available_minutes"],
    start_date: formatISODate(),
    text: "Available minutes desc"
  });

  const { selectedOrder, setSelectedOrder, noOrderSelected } = useStorageOrder({
    storagePrefix,
    defaultValue: defaultOrder
  });

  const [params, setParams] = useState<Partial<Params>>({});

  // fetch metadata to render filters if no filters are selected.
  // Ordering profiles by availability without any filters takes too much time
  const { data: defaultData } = useProfiles(initParams);

  const defaultFilters = useMemo(
    () => (defaultData ? getValueFilters(defaultData.meta.filters || {}) : {}),
    [defaultData]
  );
  const { selectedFilters, onFiltersChange, onFilterChange, onFiltersReset, noFiltersSelected } =
    useStorageFilters({
      storagePrefix,
      defaultValue: defaultFilters
    });

  useEffect(() => {
    if (isEmpty(getAPIFilters(selectedFilters))) {
      onFiltersChange(getValueFilters(defaultData?.meta.filters || {}));
    }
  }, [defaultData]);

  const planner = useProfiles(
    noFiltersSelected
      ? initParams
      : {
          ...defaultParams,
          ...params,
          order: selectedOrder as OrderOption,
          filters: selectedFilters
        },
    {
      keepPreviousData: true
    }
  );

  const loaded = !!planner.data;

  useEffect(() => {
    mergePage({
      planner: {
        ...page.planner,
        loaded,
        loading: planner.isFetching,
        data: planner.data || initialData,
        selectedFilters,
        onFiltersChange,
        onFilterChange,
        onFiltersReset,
        noFiltersSelected,
        selectedOrder,
        setSelectedOrder,
        noOrderSelected,
        params: { ...defaultParams, ...params },
        setParams
      }
    });
  }, [selectedFilters, planner.data, loaded, planner.isFetching, noOrderSelected, noFiltersSelected]);

  return page.planner;
};
